import type { AcceptedMission } from "./acceptedMissions";
import type { CombatSnapshot } from "./combatSnapshot";
import type { ClockState } from "./gameClock";
import type { BaseRecord } from "./baseStorage";
import type { NodeRegenRecord } from "./resourceNodeRegen";
import type { FactionStanding } from "./factionStanding";
import type { CaptainSkillXp } from "./captainSkills";
import type { CaptainStats } from "./captainStats";
import type { WeaponUpgrades } from "./weaponUpgrades";
import type { ShipDamageMap } from "./shipDamage";
import type { BestiaryRecord } from "./bestiary";

// Client for the existing shared save backend (CLAUDE.md) --
// /auth/save.ashx?game=starforge. GET returns {save: RawSaveRecord | null}
// (null for a brand-new player); POST body is {state, expectedVersion} for
// optimistic-concurrency conflict detection (409, plain-text body, if
// another session saved first) and on success returns
// {ok: true, save: RawSaveRecord}. Confirmed live against the real backend:
// the server's JSON is PascalCase ({Version, UpdatedAt, State}), not the
// lowercase shape used internally -- normalizeSaveRecord() is the one place
// that translates between them.

// Fleet ships are persisted the same way currentShip already is -- by
// name/variant, rehydrated against the fixed roster on load, not the full
// PlayerShip object (the roster itself is static, never per-save data).
export interface FleetShipSaveData {
  id: string;
  shipName: string;
  shipVariant: string;
  source: "owned" | "hired";
  stance: "Active" | "Passive";
  lastUpkeepAt?: number;
  // 16.72's generalized dock key. `dockedAtBaseKey` is the pre-16.72
  // Base-only name, still read (never written) so existing saves keep
  // their parked ships -- the VALUE format is identical, only the reach
  // widened, so migration is a rename and nothing more.
  dockedAtKey?: string;
  /** @deprecated pre-16.72 name; read-only for migration. */
  dockedAtBaseKey?: string;
}

export interface GameSaveData {
  shipName: string | null;
  shipVariant: string | null;
  currentShipInstanceId: string | null;
  weaponUpgrades: WeaponUpgrades;
  // Persistent hull/shield damage (16.81). Optional on read: every save
  // written before 16.81 has no damage block, and loads as an undamaged
  // fleet rather than failing -- which is also exactly what those saves
  // meant, since damage couldn't survive travel at all back then.
  shipDamage?: ShipDamageMap;
  // Opponent sightings/kills and which discovery paragraphs have been
  // shown (16.82). Both optional on read -- pre-16.82 saves load as a
  // captain who has met nobody and found nothing, which is the honest
  // reading: neither was being recorded before this.
  bestiary?: BestiaryRecord;
  loreSeenSystemIds?: string[];
  // New-captain welcome+tutorial dismissal (optional, like every field added
  // after the first save format -- an older save simply reads as false and
  // shows the tutorial once).
  hasSeenWelcome?: boolean;
  stationPurchases?: Record<string, number>;
  tradeStandingAwarded?: Record<string, number>;
  currentSystemId: string;
  currentLocationId: string;
  ownedLeases: string[];
  bases: BaseRecord[];
  nodeRegen: NodeRegenRecord[];
  standing: FactionStanding;
  // Captain Skills XP (16.69). Optional on read for backward
  // compatibility: every save written before 16.69 simply has no skills
  // block yet, and loads as a zeroed captain rather than failing.
  captainSkills?: CaptainSkillXp;
  // Lifetime career stats (16.76). Optional on read: pre-16.76 saves have
  // no record, and load as a fresh zeroed career rather than failing.
  stats?: CaptainStats;
  visitedSystemIds: string[];
  detectedHiddenSystemIds: string[];
  lastTopLevelView: "expedition" | "base" | "station" | null;
  clock: ClockState;
  fuelOnBoard: number;
  fuelCapacity: number;
  provisionsOnBoard: number;
  provisionsCapacity: number;
  provisionsLastUpdatedAt: number;
  provisionsDepletedAt: number | null;
  mutinyWarningsIssued: number;
  inEscapePod: boolean;
  isLoanerShip: boolean;
  credits: number;
  cargo: Record<string, number>;
  acceptedMissions: AcceptedMission[];
  waypointSystemId: string | null;
  // An interrupted fight (16.99). Optional so every save written before this
  // section still loads -- absent simply means "no fight was in progress".
  combatSnapshot?: CombatSnapshot | null;
  fleet: FleetShipSaveData[];
}

export interface SaveRecord {
  state: GameSaveData;
  version: number;
  // Server's own last-write timestamp (ISO string), for 16.64's save-
  // comparison UI -- previously received and discarded.
  updatedAt: string | null;
}

interface RawSaveRecord {
  Version: number;
  State: GameSaveData;
  UpdatedAt?: string;
}

export type SaveResult =
  | { outcome: "saved"; version: number }
  | { outcome: "conflict"; serverSave: SaveRecord }
  // The server session is gone (expired, revoked, or the cookie was
  // dropped) even though the client still thinks it's signed in (16.74).
  // Distinct from a generic error because the correct response is
  // different: stop trying, downgrade to local-only, and say so once --
  // retrying can never succeed until the captain logs in again.
  | { outcome: "unauthorized" }
  | { outcome: "error"; error: string };

const GAME = "starforge";

function normalizeSaveRecord(raw: RawSaveRecord | null | undefined): SaveRecord | null {
  if (!raw) {
    return null;
  }
  return { state: raw.State, version: raw.Version, updatedAt: raw.UpdatedAt ?? null };
}

export async function loadSave(): Promise<SaveRecord | null> {
  try {
    const res = await fetch(`/auth/save.ashx?game=${GAME}`, { credentials: "same-origin" });
    if (!res.ok) {
      return null;
    }
    const body = await res.json();
    return normalizeSaveRecord(body?.save);
  } catch {
    return null;
  }
}

// A 409 loads the server's copy and hands it back to the caller alongside
// the conflict outcome, with no attempt to reconcile the two -- this layer
// never merges. What the CALLER does with it changed in 16.69: it used to
// apply the server's copy outright ("server wins", discarding local
// changes since the last successful save); persistence.ts now parks both
// and routes the captain to Compare Saves to choose. This function's own
// contract is unchanged.
export async function saveGame(state: GameSaveData, expectedVersion: number): Promise<SaveResult> {
  try {
    const res = await fetch(`/auth/save.ashx?game=${GAME}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state, expectedVersion })
    });

    if (res.status === 409) {
      const serverSave = await loadSave();
      if (!serverSave) {
        return { outcome: "error", error: "Save conflict, but couldn't load the server's copy." };
      }
      return { outcome: "conflict", serverSave };
    }

    if (res.status === 401 || res.status === 403) {
      return { outcome: "unauthorized" };
    }

    if (!res.ok) {
      return { outcome: "error", error: `Save failed (${res.status}).` };
    }

    const body = await res.json().catch(() => null);
    const version = typeof body?.save?.Version === "number" ? body.save.Version : expectedVersion + 1;

    return { outcome: "saved", version };
  } catch {
    return { outcome: "error", error: "Couldn't reach the save server." };
  }
}
